/**
 * @file DesktopWidgetPage.tsx
 * @brief The desktop app's floating session widget and the popup above it.
 *
 * @details
 * Loaded by DESKTOP/src/widget.js into two small frameless windows:
 * - `?view=pill`: the always-on-top pill (DES mockups 02-10-2026). The
 *   red brand tab with the white mark is the live indicator (grey when paused);
 *   the timer; expand / pause / stop; for Boardroom the Decision and To-do
 *   bookmarks plus icon-only Agenda and Notes; for the Interpreter the latest
 *   translation as a one-line caption.
 * - `?view=panel`: the same-width popup: agenda (read only), notes (write or
 *   attach), or the end confirmation.
 *
 * Both are views of the state the session page publishes
 * (useDesktopSession) and remotes for its controls. Nothing here records,
 * plays or bills: closing or crashing these windows cannot touch the session.
 *
 * Meeting Notes (ADR-030, DES 07-10-2026, as the phone's recorder): no
 * Decision / To-do marks and no Agenda; the second row is one full-width
 * "Take notes" button; the notes popup has the meeting title field and no
 * attach; the end confirmation reads "End meeting notes?".
 *
 * Always the dark pill, in both themes: it floats over other apps, not over
 * our page, so it matches the docks' dark glass rather than the page theme.
 *
 * @author DES
 * @date 02-10-2026
 */

import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import {
  Bookmark,
  ChevronLeft,
  ChevronRight,
  CircleCheckBig,
  Ellipsis,
  Maximize2,
  Mic,
  Pause,
  PenLine,
  Play,
  Square,
  Volume2,
} from "lucide-react";
import { AgendaFlag } from "@/components/icons/AgendaFlag";
import { BrandIcon } from "@/components/brand/BrandIcon";
import { LanguageFlag } from "@/components/ui/LanguageFlag";
import { cn } from "@/shared/theme/cn";
import {
  desktopShell,
  type DesktopPanel,
  type DesktopSession,
} from "@/shared/desktop/desktopShell";
import { send, W } from "./widgetUi";
import { WidgetAgendaPanel } from "./WidgetAgendaPanel";
import { MeetingNotesPanel } from "@/features/notes/MeetingNotesPanel";
import { DockBorderTimer } from "@/features/employee-boardroom/DockBorderTimer";
import { SwipeButton } from "@/components/ui/SwipeButton";
import { recorderDockOn } from "@/features/employee-boardroom/boardroomRecorderUiPreview";

/**
 * The widget as a small version of the recorder dock (DES 11-10-2026: "make
 * this dock our widget as well"); the standard since W10 (22-10-2026).
 * The same rule as the recorder page (recorderDockOn), but only in a Mac app
 * that can widen the widget window (`reportWidth`, DESKTOP 3f823b5): an older
 * build would squeeze the open dock into its fixed 470px, so it keeps the
 * old pill. No shell (the browser mock) = the page's rule alone.
 */
function useDockStyle(): boolean {
  const shell = desktopShell();
  return recorderDockOn() && (!shell || typeof shell.reportWidth === "function");
}

const drag = { WebkitAppRegion: "drag" } as CSSProperties;
const noDrag = { WebkitAppRegion: "no-drag" } as CSSProperties;

/** The pill's red tab, cut where the see-through pill sits over it (DES
 *  13-10-2026): left of 72px, plus the two 24px corner wedges the pill's
 *  round corners leave open. Layers add up. */
const PILL_TAB_CUT = [
  "linear-gradient(to right, #000 72px, transparent 72px)",
  "radial-gradient(circle 24px at 100% 100%, transparent 23.5px, #000 24px)",
  "radial-gradient(circle 24px at 100% 0, transparent 23.5px, #000 24px)",
].join(", ");
const PILL_TAB_CUT_POS = "0 0, 72px 0, 72px 100%";
const PILL_TAB_CUT_SIZE = "100% 100%, 24px 24px, 24px 24px";
const PILL_TAB_MASK = {
  WebkitMaskImage: PILL_TAB_CUT,
  WebkitMaskPosition: PILL_TAB_CUT_POS,
  WebkitMaskSize: PILL_TAB_CUT_SIZE,
  WebkitMaskRepeat: "no-repeat",
  maskImage: PILL_TAB_CUT,
  maskPosition: PILL_TAB_CUT_POS,
  maskSize: PILL_TAB_CUT_SIZE,
  maskRepeat: "no-repeat",
} as CSSProperties;

function formatClock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = String(m).padStart(2, "0");
  const ss = String(s).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

/** The session clock, ticking locally between publishes. */
function useSessionClock(session: DesktopSession | null): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!session?.running) return;
    const id = window.setInterval(() => setNow(Date.now()), 500);
    return () => window.clearInterval(id);
  }, [session?.running]);
  if (!session) return 0;
  return session.elapsedMs + (session.running ? Math.max(0, now - session.at) : 0);
}

/** Round icon button, the pill's 30px control. */
function RoundButton({
  label,
  onClick,
  tone = "grey",
  active = false,
  size = 30,
  children,
}: {
  label: string;
  onClick: () => void;
  tone?: "grey" | "red";
  active?: boolean;
  /** px; the one-row dock widget uses 36 so its row lines up. */
  size?: number;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      style={{ ...noDrag, width: size, height: size }}
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded-full transition-colors",
        tone === "red"
          ? "bg-brand text-white hover:bg-brand/90"
          : active
            ? W.chipOn
            : W.chip,
      )}
    >
      {children}
    </button>
  );
}

function Pill({
  session,
  panel,
  onScreen,
}: {
  session: DesktopSession;
  panel: DesktopPanel | null;
  /** The pill window is actually on screen (told by the desktop shell). */
  onScreen: boolean;
}) {
  const elapsed = useSessionClock(session);
  const paused = session.status === "paused";
  const busy = session.status === "busy";
  const boardroom = session.kind === "boardroom";
  const notesTier = boardroom && session.tier === "notes";

  // "To-do marked at 12:51", for two seconds after a tap.
  const [flash, setFlash] = useState<string | null>(null);
  const flashTimer = useRef<number | null>(null);
  const mark = (kind: "decision" | "todo") => {
    send({ type: "bookmark", kind });
    setFlash(`${kind === "todo" ? "To-do" : "Decision"} marked at ${formatClock(elapsed)}`);
    if (flashTimer.current) window.clearTimeout(flashTimer.current);
    flashTimer.current = window.setTimeout(() => setFlash(null), 2000);
  };

  // The red tab slides out from behind the pill, once per session, the
  // first time the widget is actually on screen (05-10-2026, DES: "pehlay
  // sirf grey wala appear ho, phir red wala smoothly slide ho, sirf aik
  // dafa"). The pill window lives for the whole app and is often prepared
  // hidden; the pill itself mounts per session.
  const tabRef = useRef<HTMLButtonElement | null>(null);
  const introPlayed = useRef(false);
  useEffect(() => {
    const el = tabRef.current;
    if (!onScreen || introPlayed.current || !el) return;
    introPlayed.current = true;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    el.animate([{ transform: "translateX(72px)" }, { transform: "translateX(0)" }], {
      duration: 520,
      delay: 220,
      easing: "cubic-bezier(0.22, 1, 0.36, 1)",
      fill: "backwards", // tucked under the pill during the delay
    });
  }, [onScreen]);

  const decisions = session.bookmarks?.filter((b) => b.type === "decision").length ?? 0;
  const todos = session.bookmarks?.filter((b) => b.type === "todo").length ?? 0;
  const canMark = !busy;

  return (
    /* Brand tab behind the pill's left end (05-10-2026, DES's mockup): red
       with the white mark, grey while paused. Only its LEFT corners are
       round: its square right end runs under the pill, so the red fills
       behind the pill's round corners instead of reading as a second card
       peeking out. Tapping the tab opens the full window. */
    <div className="relative w-full" style={drag}>
      <button
        ref={tabRef}
        type="button"
        aria-label="Open Verblizr"
        title="Open Verblizr"
        onClick={() => desktopShell()?.restoreMain?.()}
        style={{ ...noDrag, ...PILL_TAB_MASK }}
        className={cn(
          "absolute inset-y-0 left-0 flex w-[96px] items-center rounded-l-[24px] pl-[13px]",
          paused ? "bg-[#3A3938]" : "bg-brand",
        )}
      >
        <BrandIcon src="/brand/logosvg.svg" size={46} className="brightness-0 invert" />
      </button>
      <div
        style={drag}
        className={cn(
          "relative ml-[72px] flex min-w-0 flex-col gap-2.5 rounded-[24px] p-2.5 text-[13px]",
          paused ? "bg-[#2A2928]/80 text-[#f2f2f2]" : W.surface,
        )}
      >
      <div className="flex min-w-0 items-center gap-2.5">
        {boardroom ? (
          <span className={cn("min-w-0 flex-1 truncate", paused && W.muted)}>
            {paused ? "Paused" : session.title}
          </span>
        ) : (
          /* Interpreter (05-10-2026, DES): the pair as round flags, then the
             status chip, both up here, so the second row is only the caption
             and the pill keeps one height whether live or paused. */
          <span className="flex min-w-0 flex-1 items-center gap-2">
            {session.langFlags?.length ? (
              <span className="flex shrink-0 items-center" title={session.langs}>
                {session.langFlags.map((f, i) => (
                  <LanguageFlag
                    key={`${f.code}-${i}`}
                    language={f}
                    size="sm"
                    className={cn(
                      paused ? "border-[#2A2928] ring-[#2A2928]" : "border-[#232221] ring-[#232221]",
                      "ring-2",
                      i > 0 && "-ml-1.5",
                    )}
                  />
                ))}
              </span>
            ) : (
              <span className={cn("shrink-0", paused && W.muted)}>{session.langs || session.title}</span>
            )}
            {paused || session.statusLabel ? (
              <span
                className={cn(
                  "inline-flex min-w-0 items-center gap-1 truncate whitespace-nowrap rounded-full px-2 py-0.5 text-[11px]",
                  W.chip,
                )}
              >
                {paused ? (
                  <Pause className="h-3 w-3 shrink-0" />
                ) : session.statusLabel === "Listening" ? (
                  <Mic className="h-3 w-3 shrink-0" />
                ) : (
                  <Volume2 className="h-3 w-3 shrink-0" />
                )}
                {paused ? "Paused" : session.statusLabel}
              </span>
            ) : null}
          </span>
        )}
        <span className={cn("shrink-0 font-mono tabular-nums", paused && W.muted)}>
          {formatClock(elapsed)}
        </span>
        <RoundButton label="Open full window" onClick={() => desktopShell()?.restoreMain?.()}>
          <Maximize2 className="h-4 w-4" />
        </RoundButton>
        <RoundButton
          label={paused ? "Resume" : "Pause"}
          tone={paused ? "red" : "grey"}
          onClick={() => !busy && send({ type: "toggle-hold" })}
        >
          {paused ? <Play className="ml-0.5 h-4 w-4 fill-current" /> : <Pause className="h-4 w-4" />}
        </RoundButton>
        <RoundButton
          label={boardroom ? "End meeting" : "End session"}
          tone={paused ? "grey" : "red"}
          active={panel === "end"}
          onClick={() => desktopShell()?.togglePanel?.("end")}
        >
          <Square className="h-3.5 w-3.5 fill-current" />
        </RoundButton>
      </div>

      {notesTier ? (
        /* Meeting Notes: the only button, full width (the phone's 625c775). */
        <button
          type="button"
          style={noDrag}
          aria-pressed={panel === "notes"}
          onClick={() => desktopShell()?.togglePanel?.("notes")}
          className={cn(
            "inline-flex w-full items-center justify-center gap-1.5 rounded-full px-2.5 py-1.5 text-xs",
            W.chip,
          )}
        >
          <PenLine className="h-3.5 w-3.5 text-brand" />
          Take notes
        </button>
      ) : boardroom ? (
        <div className="flex min-w-0 items-center gap-2.5">
          {flash ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-brand px-2.5 py-1.5 text-xs text-white">
              <CircleCheckBig className="h-3.5 w-3.5" />
              {flash}
            </span>
          ) : (
            <>
              <button
                type="button"
                style={noDrag}
                disabled={!canMark}
                onClick={() => mark("decision")}
                className={cn("inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1.5 text-xs", W.chip)}
              >
                <Bookmark className="h-3.5 w-3.5 fill-brand text-brand" />
                Decision
                <span className="rounded-full bg-[#5f5e5a] px-1.5 text-[11px]">{decisions}</span>
              </button>
              <button
                type="button"
                style={noDrag}
                disabled={!canMark}
                onClick={() => mark("todo")}
                className={cn("inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1.5 text-xs", W.chip)}
              >
                <CircleCheckBig className="h-3.5 w-3.5 text-brand" />
                To-do
                <span className="rounded-full bg-[#5f5e5a] px-1.5 text-[11px]">{todos}</span>
              </button>
            </>
          )}
          <span className="flex-1" />
          <RoundButton
            label="Agenda"
            active={panel === "agenda"}
            onClick={() => desktopShell()?.togglePanel?.("agenda")}
          >
            {/* DES 08-10-2026: app icon, was ListChecks */}
            <AgendaFlag className="h-4 w-4" />
          </RoundButton>
          <RoundButton
            label="Notes"
            active={panel === "notes"}
            onClick={() => desktopShell()?.togglePanel?.("notes")}
          >
            <PenLine className="h-4 w-4" />
          </RoundButton>
        </div>
      ) : (
        /* One fixed-height line (tall enough for Urdu), so the pill never
           changes height with the status or an empty caption. */
        <div className="flex h-[22px] min-w-0 items-center">
          <span
            dir={session.caption?.rtl ? "rtl" : "ltr"}
            className={cn("min-w-0 flex-1 truncate text-xs leading-[22px]", W.muted)}
          >
            {session.caption?.text || "Each translation appears here."}
          </span>
        </div>
      )}
      </div>
    </div>
  );
}

/** The one-row dock widget's width; the desktop app sizes the window to it.
 *  680 for the dock + 28 of room on the right for the time pill. */
const DOCK_WIDGET_WIDTH = 708;
/** Meeting Notes, expanded: only Take notes between the title and the
 *  slide, so no empty middle (DES 11-10-2026, "same with meeting notes"). */
const DOCK_WIDGET_NOTES_WIDTH = 620;
/** Collapsed (DES 11-10-2026: "should not be that big, expand on tap"):
 *  tab 56 + the 82px dock (ring + arrow; status and time dropped DES
 *  13-10-2026) + 28 of right room. */
const DOCK_WIDGET_COMPACT_WIDTH = 166;
/** Expanded, it folds back after this long without the mouse on it. */
const DOCK_COLLAPSE_MS = 8_000;

/**
 * Boardroom pill as the recorder dock, in one row and smaller (TRIAL, DES
 * 11-10-2026: "dock hi hoo poora widget, but over all thora small size
 * main"). The red tab stays behind its left end (DES: "but i want this
 * somewhere"): the live mark and the way back to the full window. Then, as
 * the dock: the hold ring, "Recording" over the title, Key Decision and
 * To-do as icon + count, Agenda and Notes, the slide to end. The border is the minute timer
 * (DockBorderTimer, shared with the dock), the time riding its tip.
 * ⋯ opens the popup above with End and Discard (a menu cannot open outside
 * this small window).
 */
function MiniDockPill({
  session,
  panel,
  onScreen,
}: {
  session: DesktopSession;
  panel: DesktopPanel | null;
  onScreen: boolean;
}) {
  const elapsed = useSessionClock(session);
  const paused = session.status === "paused";
  const busy = session.status === "busy";
  // Interpretation in the same dock (DES 11-10-2026, "interpretation ka
  // mock up ... match the other widgets", then "haan preview main banao"):
  // the status over the language pair, then the live translation line where
  // Boardroom has its marks. No Discard: an interpretation bills time used.
  const interp = session.kind !== "boardroom";
  const notesTier = !interp && session.tier === "notes";

  // Collapsed by default: the hold ring and the arrow only; a tap
  // opens the full dock (DES 11-10-2026, user feedback: "should not be that
  // big, it should expand on tap"). It folds back by ‹, or 8s after the
  // mouse leaves while no popup (Agenda / Notes / End) is open.
  const [expanded, setExpanded] = useState(false);
  const [hovering, setHovering] = useState(false);
  // The arrow's tooltip (DES 11-10-2026). Drawn by us: the widget never
  // takes focus and macOS shows no native tooltip in an unfocused window. It
  // sits ABOVE the dock in the page's flow, so the desktop app grows the
  // window upward for it (widget:height) and the dock never moves.
  const [tip, setTip] = useState<string | null>(null);
  useEffect(() => setTip(null), [expanded]);
  useEffect(() => {
    if (!expanded || hovering || panel) return;
    const id = window.setTimeout(() => setExpanded(false), DOCK_COLLAPSE_MS);
    return () => window.clearTimeout(id);
  }, [expanded, hovering, panel]);

  // The window keeps ONE size, the open dock's; the dock animates inside it
  // (DES 11-10-2026: resizing the window on each tap jittered, "glitchy").
  // Its empty part lets clicks through to the app behind (setClickThrough),
  // switched off while the pointer is on the dock or the tab.
  const openWidth = notesTier ? DOCK_WIDGET_NOTES_WIDTH : DOCK_WIDGET_WIDTH;
  const dockWidth = expanded ? openWidth : DOCK_WIDGET_COMPACT_WIDTH;
  useEffect(() => {
    desktopShell()?.reportWidth?.(openWidth);
  }, [openWidth]);
  const solidRef = useRef<HTMLDivElement | null>(null);

  // The logo tab is the handle (DES 11-10-2026: "logo per hold kar kay
  // jahan chahoon move"): press and move = drag the widget anywhere; a tap
  // that does not move still opens the full window. Without the desktop
  // app's drag (older builds) it is the plain tap.
  const tabDrag = useRef<{ x: number; y: number; moved: boolean } | null>(null);
  const onTabDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    tabDrag.current = { x: e.screenX, y: e.screenY, moved: false };
    desktopShell()?.dragWidget?.("start");
  };
  const onTabMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    const d = tabDrag.current;
    if (!d) return;
    if (!d.moved && Math.hypot(e.screenX - d.x, e.screenY - d.y) < 4) return;
    d.moved = true;
    desktopShell()?.dragWidget?.("move");
  };
  const onTabUp = () => {
    const d = tabDrag.current;
    tabDrag.current = null;
    desktopShell()?.dragWidget?.("end");
    if (d && !d.moved) desktopShell()?.restoreMain?.();
  };
  useEffect(() => {
    const shell = desktopShell();
    if (!shell?.setClickThrough) return;
    let through: boolean | null = null;
    const set = (on: boolean) => {
      if (on === through) return;
      through = on;
      shell.setClickThrough!(on);
    };
    const onMove = (e: MouseEvent) => {
      const el = e.target instanceof Element ? e.target : null;
      set(!(el && solidRef.current?.contains(el)));
    };
    const onLeave = () => set(true);
    set(true);
    window.addEventListener("mousemove", onMove);
    document.addEventListener("mouseleave", onLeave);
    return () => {
      window.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseleave", onLeave);
      shell.setClickThrough!(false);
    };
  }, []);
  useEffect(() => () => desktopShell()?.reportWidth?.(470), []);

  const [flash, setFlash] = useState<string | null>(null);
  const flashTimer = useRef<number | null>(null);
  const mark = (kind: "decision" | "todo") => {
    send({ type: "bookmark", kind });
    setFlash(`${kind === "todo" ? "To-do" : "Decision"} marked at ${formatClock(elapsed)}`);
    if (flashTimer.current) window.clearTimeout(flashTimer.current);
    flashTimer.current = window.setTimeout(() => setFlash(null), 2000);
  };

  // The red tab's one-time slide-in, as the Pill's.
  const tabRef = useRef<HTMLButtonElement | null>(null);
  const introPlayed = useRef(false);
  useEffect(() => {
    const el = tabRef.current;
    if (!onScreen || introPlayed.current || !el) return;
    introPlayed.current = true;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    el.animate([{ transform: "translateX(56px)" }, { transform: "translateX(0)" }], {
      duration: 520,
      delay: 220,
      easing: "cubic-bezier(0.22, 1, 0.36, 1)",
      fill: "backwards",
    });
  }, [onScreen]);

  const decisions = session.bookmarks?.filter((b) => b.type === "decision").length ?? 0;
  const todos = session.bookmarks?.filter((b) => b.type === "todo").length ?? 0;
  // One height for every control in the row (DES 11-10-2026: "kia in sabki
  // height same hai"): 36px, the slide's too.
  // Marks, Agenda and Notes: bare icons, no fill (DES 11-10-2026).
  const bare =
    "relative inline-flex h-9 min-w-8 shrink-0 items-center justify-center gap-1 px-1 text-sm font-semibold transition-colors disabled:opacity-50";
  const chip = "inline-flex h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3 text-xs font-semibold";

  return (
    // py-1.5 and pr-7: room for the time pill where it rides the top, bottom
    // or right edge, centred on the line (the window is cut at the content's
    // own box; DES 11-10-2026: the pill was cut off at the right end).
    <div
      // Its own width, held to the window's RIGHT edge: the desktop app grows
      // the window to the left, so between the tap and the resize the dock
      // spills left instead of jumping (DES 11-10-2026, "shake").
      className="relative ml-auto shrink-0 pr-7 transition-[width] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none"
      style={{ width: dockWidth }}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      {/* The tooltip's room is ALWAYS there, so the window never resizes on
          hover: resizing it moved the whole widget for a frame each time
          ("poora widget shake karta hai", DES 11-10-2026). Right-aligned
          over the arrow, a small pointer down to it. */}
      <div className="pointer-events-none flex h-8 items-end justify-end pr-1" aria-hidden>
        {tip ? (
          <span className="relative whitespace-nowrap rounded-md bg-[#3A3938] px-2 py-1 text-[11px] font-medium leading-4 text-[#f2f2f2] shadow-md">
            {tip}
            <span className="absolute -bottom-1 right-2.5 h-2 w-2 rotate-45 bg-[#3A3938]" />
          </span>
        ) : null}
      </div>
      {/* The solid part: the pointer here turns click-through off. */}
      <div ref={solidRef} className="relative py-1.5" style={drag}>
      <button
        ref={tabRef}
        type="button"
        aria-label="Open Verblizr (hold and drag to move)"
        title="Open Verblizr · hold and drag to move"
        onPointerDown={onTabDown}
        onPointerMove={onTabMove}
        onPointerUp={onTabUp}
        onPointerCancel={onTabUp}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") desktopShell()?.restoreMain?.();
        }}
        style={{
          ...noDrag,
          // Cut where the dock's round left end sits (centre 56 + 26), so
          // the red does not show through the see-through dock (DES 13-10-2026).
          WebkitMaskImage: "radial-gradient(circle 26px at 82px 50%, transparent 25.5px, #000 26px)",
          maskImage: "radial-gradient(circle 26px at 82px 50%, transparent 25.5px, #000 26px)",
        }}
        className={cn(
          "absolute bottom-1.5 left-0 top-1.5 flex w-[80px] cursor-grab touch-none items-center rounded-l-full pl-[12px] active:cursor-grabbing",
          paused ? "bg-[#3A3938]" : "bg-brand",
        )}
      >
        <BrandIcon src="/brand/logosvg.svg" size={34} className="brightness-0 invert" />
      </button>
      <div
        style={drag}
        className={cn(
          "relative ml-[56px] flex h-[52px] min-w-0 items-center gap-2 rounded-full pl-2 pr-1.5 text-[13px]",
          W.surface,
        )}
      >
        <DockBorderTimer
          elapsedMs={elapsed}
          label={formatClock(elapsed)}
          // Collapsed shows no time at all (DES 13-10-2026).
          showBadge={expanded}
          held={paused}
          radius="pill"
          trackClassName="stroke-[#3A3938]"
        />
        <button
          type="button"
          style={noDrag}
          disabled={busy}
          onClick={() => send({ type: "toggle-hold" })}
          aria-label={interp ? (paused ? "Resume" : "Pause") : paused ? "Resume recording" : "Hold recording"}
          title={interp ? (paused ? "Resume" : "Pause") : paused ? "Resume" : "Hold"}
          className={cn(
            "relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 disabled:opacity-50",
            paused ? "border-[#5f5e5a]" : "border-brand",
          )}
        >
          {paused ? (
            <span className="flex gap-[3px]" aria-hidden>
              <span className="h-2.5 w-[3px] rounded-full bg-[#A3A3A3]" />
              <span className="h-2.5 w-[3px] rounded-full bg-[#A3A3A3]" />
            </span>
          ) : (
            <span className="h-2 w-2 animate-pulse rounded-full bg-brand" aria-hidden />
          )}
        </button>
        {!expanded ? (
          <>
            <button
              type="button"
              style={noDrag}
              onClick={() => setExpanded(true)}
              aria-label="Show all controls"
              title="Show all controls"
              onMouseEnter={() => setTip("Show all controls")}
              onMouseLeave={() => setTip(null)}
              // Just the ring and the arrow: no status, no time (DES 13-10-2026,
              // "collapsed stage main recording aur timer bhi nikal do").
              className="group relative flex h-9 w-6 shrink-0 items-center justify-center"
            >
              <ChevronRight
                className={cn("h-4 w-4 shrink-0 transition-colors group-hover:text-[#f2f2f2]", W.muted)}
                aria-hidden
              />
            </button>
          </>
        ) : (
        // Clipped while the dock slides open, faded in, so nothing squashes.
        <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden duration-300 animate-in fade-in-0">
        {interp ? (
          <span className="relative shrink-0">
            <span
              className={cn("block text-[9px] font-bold uppercase tracking-[0.12em]", paused ? W.muted : "text-brand")}
            >
              {paused ? "Paused" : session.statusLabel || "Live"}
            </span>
            <span className="flex items-center gap-1 text-xs font-semibold text-[#f2f2f2]/70">
              {session.langFlags?.length ? (
                <span className="flex shrink-0 items-center" title={session.langs}>
                  {session.langFlags.map((f, i) => (
                    <LanguageFlag
                      key={`${f.code}-${i}`}
                      language={f}
                      size="sm"
                      className={cn("border-[#232221] ring-2 ring-[#232221]", i > 0 && "-ml-1.5")}
                    />
                  ))}
                </span>
              ) : null}
              {session.langs || session.title}
            </span>
          </span>
        ) : (
        <span className="relative min-w-0 flex-1">
          <span
            className={cn("block text-[9px] font-bold uppercase tracking-[0.12em]", paused ? W.muted : "text-brand")}
          >
            {paused ? "On hold" : "Recording"}
          </span>
          {/* A long title takes two lines, then "…"; hover shows it whole.
              Dimmed, as the recorder's heading and dock (DES 11-10-2026). */}
          <span className="line-clamp-2 break-words text-xs leading-[1.15] text-[#f2f2f2]/70" title={session.title}>
            {session.title}
          </span>
        </span>
        )}

        {interp ? (
          <>
            <span aria-hidden className="relative h-5 w-px shrink-0 bg-[#3A3938]" />
            {/* The live translation, one line (tall enough for Urdu). */}
            <span
              dir={!paused && session.caption?.rtl ? "rtl" : "ltr"}
              className={cn(
                "relative min-w-0 flex-1 truncate px-1 text-[13px] leading-[22px]",
                paused || !session.caption?.text ? W.muted : "text-[#f2f2f2]",
              )}
            >
              {paused
                ? "Press the button to resume"
                : session.caption?.text || "Each translation appears here."}
            </span>
          </>
        ) : flash ? (
          // 28px: the icons beside it have no fill, so a 36px pill read as
          // too tall (DES 11-10-2026).
          <span className="relative inline-flex h-7 shrink-0 items-center gap-1.5 rounded-full bg-brand px-2.5 text-xs text-white">
            <CircleCheckBig className="h-3.5 w-3.5" />
            {flash}
          </span>
        ) : notesTier ? (
          <button
            type="button"
            style={noDrag}
            aria-pressed={panel === "notes"}
            onClick={() => desktopShell()?.togglePanel?.("notes")}
            className={cn("relative", chip, panel === "notes" ? W.chipOn : W.chip)}
          >
            <PenLine className="h-3.5 w-3.5 text-brand" />
            Take notes
          </button>
        ) : (
          <div className="relative flex shrink-0 items-center gap-0.5">
            {/* No circles, bigger icons, hairline dividers between them (DES
                11-10-2026: "inhain circle say nikalo, icons baray karo with
                divider", then "spacing kam karo"): one tight group, no line before
                the first icon. */}
            <button
              type="button"
              style={noDrag}
              disabled={busy}
              onClick={() => mark("decision")}
              // Icon and count only (DES 11-10-2026); the name is the tooltip.
              aria-label={`Mark a key decision (${decisions} so far)`}
              title="Key Decision"
              className={cn(bare, "text-brand hover:text-brand/80")}
            >
              <Bookmark className="h-5 w-5 fill-brand text-brand" />
              <span className="tabular-nums">{decisions}</span>
            </button>
            <span aria-hidden className="relative h-5 w-px shrink-0 bg-[#3A3938]" />
            <button
              type="button"
              style={noDrag}
              disabled={busy}
              onClick={() => mark("todo")}
              aria-label={`Mark a to-do (${todos} so far)`}
              title="To-do"
              className={cn(bare, "text-[#f2f2f2] hover:text-white")}
            >
              <CircleCheckBig className="h-5 w-5" />
              <span className="tabular-nums">{todos}</span>
            </button>
            <span aria-hidden className="relative h-5 w-px shrink-0 bg-[#3A3938]" />
            <button
              type="button"
              style={noDrag}
              aria-label="Agenda"
              title="Agenda"
              aria-pressed={panel === "agenda"}
              onClick={() => desktopShell()?.togglePanel?.("agenda")}
              className={cn(bare, panel === "agenda" ? "text-brand" : "text-[#f2f2f2] hover:text-white")}
            >
              <AgendaFlag className="h-5 w-5" />
            </button>
            <span aria-hidden className="relative h-5 w-px shrink-0 bg-[#3A3938]" />
            <button
              type="button"
              style={noDrag}
              aria-label="Notes"
              title="Notes"
              aria-pressed={panel === "notes"}
              onClick={() => desktopShell()?.togglePanel?.("notes")}
              className={cn(bare, panel === "notes" ? "text-brand" : "text-[#f2f2f2] hover:text-white")}
            >
              <PenLine className="h-5 w-5" />
            </button>
            <span aria-hidden className="relative h-5 w-px shrink-0 bg-[#3A3938]" />
          </div>
        )}

        <div className="relative w-[176px] shrink-0" style={noDrag}>
          <SwipeButton
            size="mini"
            text="Slide to end"
            loading={busy}
            loadingText="Ending…"
            disabled={busy}
            onSwipeComplete={() => send({ type: "end" })}
            accessibilityLabel={
              interp
                ? "End the interpretation"
                : notesTier
                  ? "End recording and write notes"
                  : "End recording and process AI"
            }
            accessibilityHint={
              interp ? "Slide or press Enter to end the session" : "Slide or press Enter to end the meeting"
            }
          />
        </div>
        {/* ⋯ (DES 11-10-2026: "discard walay dots add karo"): a menu cannot
            open outside this small window, so it opens the popup above,
            the same End / Discard confirmation the old pill's End used. */}
        {/* Plain dots, no circle (DES 11-10-2026). */}
        <button
          type="button"
          aria-label={interp ? "More: end" : "More: end or discard"}
          title={interp ? "More: end" : "More: end or discard"}
          aria-pressed={panel === "end"}
          onClick={() => desktopShell()?.togglePanel?.("end")}
          style={noDrag}
          className={cn(
            "relative inline-flex h-9 w-6 shrink-0 items-center justify-center transition-colors hover:text-[#f2f2f2]",
            panel === "end" ? "text-[#f2f2f2]" : W.muted,
          )}
        >
          <Ellipsis className="h-4 w-4" />
        </button>
        {/* Fold back to the small widget. */}
        <button
          type="button"
          style={noDrag}
          onClick={() => setExpanded(false)}
          onMouseEnter={() => setTip("Hide controls")}
          onMouseLeave={() => setTip(null)}
          aria-label="Hide controls"
          title="Hide controls"
          className={cn(
            "relative inline-flex h-9 w-5 shrink-0 items-center justify-center transition-colors hover:text-[#f2f2f2]",
            W.muted,
          )}
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        </div>
        )}
      </div>
      </div>
    </div>
  );
}

function EndPanel({ session }: { session: DesktopSession }) {
  const boardroom = session.kind === "boardroom";
  // A Meeting Notes recording is not a Boardroom session (the phone's 03806ae).
  const notesTier = boardroom && session.tier === "notes";
  // Discard (DES 02-10-2026, Boardroom only): a second, explicit step, red
  // only once chosen. An interpretation has no discard: it bills time used.
  const [discarding, setDiscarding] = useState(false);

  if (discarding) {
    return (
      <div className={cn("flex flex-col gap-3 rounded-[24px] px-4 py-3.5 text-[13px]", W.surface)}>
        <p className="font-medium">Discard this recording?</p>
        <p className={cn("text-xs leading-relaxed", W.muted)}>
          Nothing is processed or charged, and the hold on the card is released. This can't be
          undone.
        </p>
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => setDiscarding(false)}
            className={cn("rounded-full px-3 py-1.5 text-xs", W.muted, "hover:text-[#f2f2f2]")}
          >
            Keep recording
          </button>
          <button
            type="button"
            onClick={() => send({ type: "discard" })}
            className="rounded-full bg-brand px-3.5 py-1.5 text-xs font-medium text-white hover:bg-brand/90"
          >
            Discard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col gap-3 rounded-[24px] px-4 py-3.5 text-[13px]", W.surface)}>
      <p className="font-medium">
        {notesTier ? "End meeting notes?" : boardroom ? "End the meeting?" : "End the session?"}
      </p>
      <p className={cn("text-xs leading-relaxed", W.muted)}>
        {notesTier
          ? "Recording stops and your notes are written from what was said."
          : boardroom
            ? "The recording uploads and Meeting Intel starts. Verblizr opens to show the progress."
            : "The interpretation stops and the session is billed."}
      </p>
      <div className="flex items-center gap-2">
        {boardroom ? (
          <button
            type="button"
            onClick={() => setDiscarding(true)}
            className={cn("rounded-full px-1 py-1.5 text-xs", W.muted, "hover:text-[#f2f2f2]")}
          >
            Discard recording
          </button>
        ) : null}
        <span className="flex-1" />
        <button
          type="button"
          onClick={() => desktopShell()?.togglePanel?.(null)}
          className={cn("rounded-full px-3 py-1.5 text-xs", W.muted, "hover:text-[#f2f2f2]")}
        >
          {boardroom ? "Keep recording" : "Keep going"}
        </button>
        <button
          type="button"
          onClick={() => send({ type: "end" })}
          className="shrink-0 whitespace-nowrap rounded-full bg-brand px-3.5 py-1.5 text-xs font-medium text-white hover:bg-brand/90"
        >
          {notesTier ? "End & write notes" : boardroom ? "End and process" : "End session"}
        </button>
      </div>
    </div>
  );
}

/** DEV ONLY: `?mock=boardroom|interpret[&panel=agenda|notes|end][&paused=1][&tier=notes][&agenda=<noteId>]` renders
 *  the widget in a browser tab with a stand-in session, to check its look. */
function devMock(): { session: DesktopSession; panel: DesktopPanel | null } | null {
  if (!import.meta.env.DEV) return null;
  const q = new URLSearchParams(window.location.search);
  const kind = q.get("mock");
  if (kind !== "boardroom" && kind !== "interpret") return null;
  const paused = q.get("paused") === "1";
  const p = q.get("panel");
  const session: DesktopSession =
    kind === "boardroom"
      ? {
          kind,
          // `&title=` to check a long title (DES 11-10-2026).
          title: q.get("title") || "Alpha Bravo Carlie",
          status: paused ? "paused" : "live",
          elapsedMs: 768_000,
          at: Date.now(),
          running: !paused,
          bookmarks: [
            { type: "decision", timestamp: "04:12" },
            { type: "todo", timestamp: "09:30" },
            { type: "todo", timestamp: "12:51" },
          ],
          // `&agenda=<noteId>`: that agenda with B49 check boxes.
          agendaNoteIds: q.get("agenda") ? [q.get("agenda") as string] : [],
          agendaChecks: q.get("agenda")
            ? ["Marketing budget", "Q4 spend", "Hiring plan", "Office move", "Customer churn"].map(
                (text, index) => ({
                  noteId: q.get("agenda") as string,
                  noteTitle: "Board prep",
                  index,
                  text,
                  parentText: index === 1 ? "Marketing budget" : null,
                  checked: index === 0 || index === 2,
                  atSecs: index === 0 ? 95 : index === 2 ? 301 : null,
                }),
              )
            : undefined,
          meetingNoteIds: [],
          writingNoteId: null,
          tier: q.get("tier") === "notes" ? "notes" : "full",
        }
      : {
          kind,
          title: "Interpreter",
          status: paused ? "paused" : "live",
          statusLabel: paused ? null : "Listening",
          elapsedMs: 159_000,
          at: Date.now(),
          running: !paused,
          langs: "EN → UR",
          caption: { text: "مجھے یقین ہے کہ آپ اس کا کوئی طریقہ جانتے ہیں", rtl: true },
        };
  return { session, panel: p === "agenda" || p === "notes" || p === "end" ? p : null };
}

export function DesktopWidgetPage() {
  const view = useMemo(
    () => (new URLSearchParams(window.location.search).get("view") === "panel" ? "panel" : "pill"),
    [],
  );
  const [session, setSession] = useState<DesktopSession | null>(null);
  const [panel, setPanel] = useState<DesktopPanel | null>(null);
  const dockStyle = useDockStyle();
  // On screen? Told by the desktop shell (the page itself always reads
  // "visible"). Without that API (browser mock, older app) assume yes.
  const [onScreen, setOnScreen] = useState(() => !desktopShell()?.onWidgetVisible);
  useEffect(() => {
    const shell = desktopShell();
    if (!shell?.onWidgetVisible) return;
    const off = shell.onWidgetVisible((v) => setOnScreen(!!v));
    void shell.getWidgetVisible?.().then((v) => {
      if (v) setOnScreen(true);
    });
    return off;
  }, []);
  const rootRef = useRef<HTMLDivElement>(null);

  // A transparent window: only the rounded pill shows.
  useEffect(() => {
    const els = [document.documentElement, document.body, document.getElementById("root")];
    const prev = els.map((el) => el?.style.background ?? "");
    els.forEach((el) => el && (el.style.background = "transparent"));
    return () => els.forEach((el, i) => el && (el.style.background = prev[i] ?? ""));
  }, []);

  useEffect(() => {
    const mock = devMock();
    if (mock) {
      setSession(mock.session);
      setPanel(mock.panel);
      return;
    }
    const shell = desktopShell();
    if (!shell?.onSession) return;
    void shell.getSession?.().then((s) => setSession(s ?? null));
    void shell.getPanel?.().then((p) => setPanel(p ?? null));
    const offS = shell.onSession((s) => setSession(s ?? null));
    const offP = shell.onPanel?.((p) => setPanel(p ?? null));
    return () => {
      offS();
      offP?.();
    };
  }, []);

  // The window fits its content.
  useEffect(() => {
    const el = rootRef.current;
    const shell = desktopShell();
    if (!el || !shell?.reportHeight) return;
    const ro = new ResizeObserver(() => shell.reportHeight!(el.getBoundingClientRect().height));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  let body: ReactNode = null;
  if (session) {
    if (view === "pill")
      body =
        dockStyle ? (
          <MiniDockPill session={session} panel={panel} onScreen={onScreen} />
        ) : (
          <Pill session={session} panel={panel} onScreen={onScreen} />
        );
    else if (panel === "end") body = <EndPanel session={session} />;
    else if (panel === "agenda") body = <WidgetAgendaPanel session={session} />;
    else if (panel === "notes")
      body = (
        <MeetingNotesPanel
          tone={import.meta.env.DEV && new URLSearchParams(window.location.search).get("tone") === "page" ? "page" : "widget"}
          meetingTitle={session.title}
          agendaNoteIds={session.agendaNoteIds ?? []}
          meetingNoteIds={session.meetingNoteIds ?? []}
          writingNoteId={session.writingNoteId ?? null}
          onChange={(meetingNoteIds, writingNoteId) =>
            send({ type: "set-notes", meetingNoteIds, writingNoteId })
          }
          onClose={() => desktopShell()?.togglePanel?.(null)}
          // The recorder's clock as the page last reported it, run forward
          // while live (ADR-030): stamps each line with the second it was typed.
          getElapsedSecs={() =>
            (session.elapsedMs + (session.running ? Math.max(0, Date.now() - session.at) : 0)) / 1000
          }
          // Meeting Notes: "Take notes", the title field, no attach.
          {...(session.tier === "notes"
            ? {
                heading: "Take notes",
                allowAttach: false,
                titleField: {
                  initial: session.notesTitle ?? "",
                  onChange: (title: string) => send({ type: "set-title", title }),
                },
              }
            : {})}
        />
      );
  }

  return (
    <div
      ref={rootRef}
      className={cn(
        "w-full select-none antialiased",
        // The pill is pinned to the window's BOTTOM, as the desktop app grows
        // that window upward: content that gets taller (the arrow's tooltip)
        // spills up, never pushes the dock down under the mouse while the
        // window catches up (DES 11-10-2026).
        view === "pill" && !devMock() && "fixed inset-x-0 bottom-0",
        // …and to its RIGHT edge (justify-end overflows to the left).
        view === "pill" && !devMock() && dockStyle && "flex justify-end",
        devMock() && "mx-auto mt-6",
        devMock() && (dockStyle && view === "pill" ? "max-w-[680px]" : "max-w-[380px]"),
      )}
    >
      {body}
    </div>
  );
}

export default DesktopWidgetPage;
